import { useRequestDetail } from "@/api/hooks/use-requests";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { ErrorState } from "@/components/ErrorState";
import { StatusBadge } from "@/components/StatusBadge";
import { formatUsd, formatTokens, formatLatency, formatTimestamp } from "@/lib/format";

interface RequestDetailProps {
  requestId: string;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-line-subtle">
      <span className="text-fg-muted">{label}</span>
      <span className="font-mono text-fg text-right max-w-[60%] truncate">
        {value ?? "\u2014"}
      </span>
    </div>
  );
}

export function RequestDetail({ requestId }: RequestDetailProps) {
  const { data, isLoading, isError, error, refetch } =
    useRequestDetail(requestId);

  if (isLoading) return <LoadingSkeleton rows={8} />;
  if (isError)
    return (
      <ErrorState
        message={error?.message ?? "Failed to load request"}
        onRetry={() => refetch()}
      />
    );
  if (!data) return null;

  const { request: req, routing } = data;

  return (
    <div className="space-y-5 text-xs">
      {/* Request metadata */}
      <section>
        <h3 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2">
          Request
        </h3>
        <div className="rounded-lg border border-line-subtle bg-canvas p-3">
          <Field label="Request ID" value={req.id} />
          <Field label="Tenant ID" value={req.tenant_id} />
          <Field label="User ID" value={req.user_id} />
          <Field label="Feature" value={req.feature} />
          <Field label="Environment" value={req.env} />
          <Field
            label="Status"
            value={req.status ? <StatusBadge status={req.status} /> : "\u2014"}
          />
          <Field label="Created" value={formatTimestamp(req.created_at)} />
        </div>
      </section>

      {/* Routing decision */}
      <section>
        <h3 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2">
          Routing Decision
        </h3>
        <div className="rounded-lg border border-line-subtle bg-canvas p-3">
          {routing ? (
            <>
              <Field
                label="Chosen Model"
                value={routing.chosen_model ?? "\u2014"}
              />
              <Field label="Reason Code" value={routing.reason_code} />
              <Field label="Policy ID" value={routing.policy_id} />
              <Field label="Rule ID" value={routing.rule_id} />
              <Field
                label="Status"
                value={
                  routing.status ? (
                    <StatusBadge status={routing.status} />
                  ) : (
                    "\u2014"
                  )
                }
              />
              <Field
                label="Cache Hit"
                value={routing.cache_hit ? "Yes" : "No"}
              />
              <Field label="Latency" value={formatLatency(routing.latency_ms)} />
            </>
          ) : (
            <p className="py-3 text-center text-fg-muted">
              No routing event
            </p>
          )}
        </div>
      </section>

      {/* Token / cost breakdown */}
      <section>
        <h3 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2">
          Tokens &amp; Cost
        </h3>
        <div className="rounded-lg border border-line-subtle bg-canvas p-3">
          <Field label="Requested Model" value={req.requested_model} />
          <Field
            label="Used Model"
            value={req.used_model ?? "\u2014"}
          />
          <Field label="Action" value={req.action} />
          <div className="mt-2 pt-2 border-t border-line-subtle">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div />
              <span className="text-fg-muted font-medium">Estimated</span>
              <span className="text-fg-muted font-medium">Actual</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center py-1">
              <span className="text-fg-muted text-left">Input Tokens</span>
              <span className="font-mono text-fg">
                {req.input_tokens_est != null
                  ? formatTokens(req.input_tokens_est)
                  : "\u2014"}
              </span>
              <span className="font-mono text-fg">
                {req.input_tokens_actual != null
                  ? formatTokens(req.input_tokens_actual)
                  : "\u2014"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center py-1">
              <span className="text-fg-muted text-left">Output Tokens</span>
              <span
                className="font-mono text-fg-faint"
                title="Output size is only known after the response. The pre-request estimate is a fixed max-tokens ceiling (e.g. 16384 for reasoning models), not a prediction, so it isn't shown. Cost is always billed on actual tokens."
              >
                {"\u2014"}
              </span>
              <span className="font-mono text-fg">
                {req.output_tokens_actual != null
                  ? formatTokens(req.output_tokens_actual)
                  : "\u2014"}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center py-1 border-t border-line-subtle">
              <span className="text-fg-muted text-left">Cost</span>
              <span
                className="font-mono text-fg-faint"
                title="Estimated cost is derived from the pre-request max-tokens ceiling (not a real prediction), so it isn't shown. Cost is always billed on actual tokens."
              >
                {"\u2014"}
              </span>
              <span className="font-mono text-fg">
                {req.cost_actual ? formatUsd(req.cost_actual) : "\u2014"}
              </span>
            </div>
          </div>
          <Field label="Latency" value={formatLatency(req.latency_ms)} />
        </div>
      </section>

      {/* Cache info */}
      <section>
        <h3 className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-2">
          Cache
        </h3>
        <div className="rounded-lg border border-line-subtle bg-canvas p-3">
          <Field
            label="Cache Hit"
            value={req.cache_hit != null ? (req.cache_hit ? "Yes" : "No") : "\u2014"}
          />
          <Field label="Cache Type" value={req.cache_type} />
        </div>
      </section>
    </div>
  );
}
